import React, { lazy, Suspense } from 'react';
import { Control, Controller } from 'react-hook-form';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useCampaignAssets, useCampaignMutations } from '../../../../hooks/campaign-hooks';
import { CampaignAsset, StepFileRef } from '../../../../support/campaigns';
import { getCampaignAssetPreview } from '../../../../support/campaignAssets';
import { GhostButton } from './primitives';
import { AssetFileField } from './AssetFileField';
import { nextOrderOf } from './builderShared';

// pdf.js (pulled in by PdfSlideViewer) is large and most steps have no deck to preview, so defer it
// — same reasoning as the ResourceFileViewer lazy import. Reused verbatim; PDF rendering is not
// reimplemented here.
const PdfSlideViewer = lazy(
  () => import('../../../Enablement/JourneyMap/PdfSlideViewer'),
);

/**
 * The file slots each RUNNER kind actually consumes at runtime, with a human label per role.
 *
 * These slot names are LOAD-BEARING: the runner (`guided/api/resolveContent.ts` +
 * `guided/composition/primitives/*`) locates each file by its exact slot name and binds it to a
 * specific download/present control — the conversation guide is `propose`'s `deck` slot, the email
 * template is its `email` slot, etc. So the set is fixed per kind: the author fills known, labeled
 * roles rather than inventing a slot string and separately pointing it at an asset. (The old free-text
 * slot + asset-key picker let an author type e.g. `pitch`/`workshop` — deck-id fallback *hints*, never
 * real slots — and the file was then silently never read.) `assetKind` seeds a freshly-created
 * campaign asset's `kind` for that role.
 */
type FileSlotDef = { slot: string; label: string; hint: string; assetKind: string };

const FILE_SLOT_CATALOG: Record<string, FileSlotDef[]> = {
  // ConversationGuide (propose.tsx): deck / email / onepager / onepager-security.
  propose: [
    { slot: 'deck', label: 'Conversation guide', hint: 'The pitch / conversation deck handed to the SE.', assetKind: 'deck' },
    { slot: 'email', label: 'Email template', hint: 'Follow-up email template (.docx).', assetKind: 'doc' },
    { slot: 'onepager', label: 'Infrastructure one-pager', hint: 'Infrastructure one-pager handout (.pdf).', assetKind: 'pdf' },
    { slot: 'onepager-security', label: 'Security one-pager', hint: 'Security one-pager handout (.pdf).', assetKind: 'pdf' },
  ],
  // SessionDeck (workshopDeck.tsx): the workshop presentation lives in the `deck` slot.
  workshop: [
    { slot: 'deck', label: 'Workshop deck', hint: 'The session presentation deck.', assetKind: 'deck' },
  ],
  // DeepDivePanel: the scenario cheat sheet lives in the `cheatsheet` slot.
  deepdive: [
    { slot: 'cheatsheet', label: 'Scenario cheat sheet', hint: 'Deep-dive scenario cheat-sheet handout.', assetKind: 'pdf' },
  ],
};

/**
 * Read a step's `metadata.files` into the form shape (tolerant of missing/typed-wrong fields). Mirrors
 * `readRunnerContent` in `RunnerContentFields`.
 */
export const readStepFiles = (raw: unknown): StepFileRef[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((r) => r && typeof r === 'object')
    .map((r) => {
      const o = r as Record<string, unknown>;
      return {
        slot: typeof o.slot === 'string' ? o.slot : '',
        assetKey: typeof o.assetKey === 'string' ? o.assetKey : '',
        kind: typeof o.kind === 'string' && o.kind ? o.kind : undefined,
        label: typeof o.label === 'string' && o.label ? o.label : undefined,
      };
    });
};

/** The file references worth persisting — a row needs both a slot and a picked asset. */
export const cleanStepFiles = (files: StepFileRef[] | undefined): StepFileRef[] =>
  (files ?? [])
    .map((f) => {
      const label = (f.label ?? '').trim();
      return {
        slot: (f.slot ?? '').trim(),
        assetKey: (f.assetKey ?? '').trim(),
        ...(f.kind ? { kind: f.kind } : {}),
        ...(label ? { label } : {}),
      };
    })
    .filter((f) => f.slot && f.assetKey);

const PREVIEW_POLL_MAX = 5;
const PREVIEW_POLL_MS = 2500;

/**
 * In-editor preview of an asset's file, driven by the campaign-asset preview endpoint.
 *
 * The pptx→PDF conversion is asynchronous, so this polls a few times while `processing` (like the
 * resource-file viewer) then shows a "converting…" note. When `ready` + `pdf` it fetches the signed
 * `preview_url` into a same-origin `blob:` URL — the deployment's COEP `require-corp` refuses a
 * cross-origin S3 URL in an embed — and renders it a slide at a time through the reused
 * `PdfSlideViewer`. `native` (an already-viewable pdf/image) is framed from the same blob; `none`
 * and `failed` fall back to a plain note (the row still has Download on the file field).
 */
function StepFilePreview(props: { asset: CampaignAsset }): JSX.Element | null {
  const { asset } = props;
  const [open, setOpen] = React.useState(false);
  const [phase, setPhase] = React.useState<
    'idle' | 'loading' | 'processing' | 'pdf' | 'native' | 'none' | 'error'
  >('idle');
  const [blobUrl, setBlobUrl] = React.useState('');
  const [error, setError] = React.useState('');
  const urlRef = React.useRef('');

  const revoke = React.useCallback(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = '';
    }
  }, []);

  // Close and drop any blob when the underlying file changes (id or object key), so a replaced or
  // repointed file never shows the previous bytes.
  React.useEffect(() => {
    setOpen(false);
    setPhase('idle');
    setError('');
    setBlobUrl('');
    revoke();
  }, [asset.id, asset.object_key, revoke]);

  React.useEffect(() => () => revoke(), [revoke]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const run = async () => {
      setPhase('loading');
      setError('');

      try {
        for (let attempt = 0; ; attempt += 1) {
          const preview = await getCampaignAssetPreview(asset.id);
          if (cancelled) return;

          if (preview.preview_status === 'processing') {
            setPhase('processing');
            // A few polls, then leave the "converting…" note up rather than spinning forever;
            // re-opening the preview shortly should find it ready.
            if (attempt + 1 >= PREVIEW_POLL_MAX) return;
            await new Promise((r) => setTimeout(r, PREVIEW_POLL_MS));
            continue;
          }

          if (preview.preview_status === 'ready' && preview.preview_url) {
            // COEP require-corp blocks a cross-origin S3 URL in an <iframe>/pdf embed — read the
            // bytes and point the renderer at a same-origin blob: URL instead.
            const resp = await fetch(preview.preview_url);
            if (!resp.ok) throw new Error(`Preview read failed (${resp.status}).`);
            const blob = await resp.blob();
            if (cancelled) return;
            revoke();
            const url = URL.createObjectURL(blob);
            urlRef.current = url;
            setBlobUrl(url);
            setPhase(preview.render_kind === 'native' ? 'native' : 'pdf');
            return;
          }

          // 'failed' | 'none' (or 'ready' with no url) — nothing to render.
          setPhase('none');
          return;
        }
      } catch (e) {
        if (!cancelled) {
          setError((e as Error)?.message ?? 'Preview could not be loaded.');
          setPhase('error');
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [open, asset.id, asset.object_key, revoke]);

  if (!asset.object_key) return null;

  return (
    <Stack spacing={1}>
      <Box>
        <Button
          size="small"
          variant="text"
          startIcon={<VisibilityOutlinedIcon fontSize="small" />}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? 'Hide preview' : 'Preview'}
        </Button>
      </Box>

      {open && (
        <Box>
          {(phase === 'loading' || phase === 'processing') && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                {phase === 'processing'
                  ? 'Converting the deck for preview — check back shortly. You can download it now.'
                  : 'Loading preview…'}
              </Typography>
            </Stack>
          )}

          {phase === 'error' && <Alert severity="warning">{error} Download it to open it.</Alert>}

          {phase === 'none' && (
            <Typography variant="caption" color="text.secondary">
              No in-editor preview is available for this file. Download it to open it.
            </Typography>
          )}

          {phase === 'pdf' && blobUrl && (
            <Suspense
              fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={18} />
                </Box>
              }
            >
              <PdfSlideViewer src={blobUrl} title={asset.title || asset.key} heightPx={520} />
            </Suspense>
          )}

          {phase === 'native' && blobUrl && (
            <iframe
              src={blobUrl}
              title={asset.title || asset.key || 'File preview'}
              style={{
                width: '100%',
                height: '520px',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 4,
                background: '#1A1A1A',
              }}
            />
          )}
        </Box>
      )}
    </Stack>
  );
}

/**
 * A single labeled file slot for a step: the human role (e.g. "Conversation guide"), the file that
 * fills it, and the three actions the author needs — upload a new file, pick an existing campaign
 * asset, or clear the slot. Under the hood a filled slot is still a `StepFileRef { slot, assetKey }`
 * on `step.metadata.files` (the shape the runner reads); this widget just hides the slot-string /
 * asset-key indirection behind the named role.
 */
function FileSlotRow(props: {
  def: { slot: string; label: string; hint: string; assetKind: string };
  refRow: StepFileRef | undefined;
  asset: CampaignAsset | undefined;
  assetRows: CampaignAsset[];
  campaignId: number;
  disabled?: boolean;
  creating: boolean;
  onCreateAsset: (assetKind: string) => Promise<string | null>;
  onSetRef: (patch: Partial<StepFileRef> & { assetKey: string }) => void;
  onPatchRef: (patch: Partial<StepFileRef>) => void;
  onClear: () => void;
  onAssetUploaded: (asset: CampaignAsset, next: Partial<CampaignAsset>) => void;
}): JSX.Element {
  const {
    def, refRow, asset, assetRows, campaignId, disabled, creating,
    onCreateAsset, onSetRef, onPatchRef, onClear, onAssetUploaded,
  } = props;

  // "Add a new file" = create an empty campaign asset for this role and point the slot at it; the
  // asset list refetches and the `<AssetFileField>` below then renders, ready for the byte upload.
  const addNewFile = async () => {
    const key = await onCreateAsset(def.assetKind);
    if (key) onSetRef({ assetKey: key, kind: def.assetKind });
  };

  const missingKey = !!refRow?.assetKey && !asset;

  return (
    <Stack
      spacing={1}
      sx={{ p: 1.25, borderRadius: 1, border: '1px solid rgba(255,255,255,0.08)' }}
    >
      <Stack direction="row" alignItems="baseline" spacing={1} justifyContent="space-between">
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {def.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {def.hint}
          </Typography>
        </Box>
        {!disabled && (refRow?.assetKey || asset) && (
          <Tooltip title="Clear this file">
            <IconButton size="small" onClick={onClear}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {asset ? (
        <>
          <AssetFileField
            row={asset}
            campaignId={campaignId}
            canWrite={!disabled}
            onUploaded={(next) => onAssetUploaded(asset, next)}
          />
          <StepFilePreview asset={asset} />
          {/* Optional author override for the on-screen heading the SE sees (blank → the runner's
              shipped default). Kept secondary — most roles never need it. */}
          <TextField
            label="Heading shown to the SE (optional)"
            size="small"
            fullWidth
            value={refRow?.label ?? ''}
            disabled={disabled}
            onChange={(e) => onPatchRef({ label: e.target.value })}
          />
        </>
      ) : missingKey ? (
        <Typography variant="caption" color="warning.main">
          Referenced file “{refRow?.assetKey}” is missing. Upload a new file or pick an existing one.
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          No file yet.
        </Typography>
      )}

      {!disabled && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {!asset && (
            <GhostButton onClick={addNewFile} disabled={creating}>
              {creating ? 'Adding…' : '+ Upload a new file'}
            </GhostButton>
          )}
          {/* Pick an existing campaign asset (reuse a file already uploaded for another step/role). */}
          {assetRows.length > 0 && (
            <TextField
              select
              label={asset ? 'Use a different existing file' : 'Or pick an existing file'}
              size="small"
              sx={{ minWidth: 240 }}
              value={refRow?.assetKey && asset ? refRow.assetKey : ''}
              disabled={disabled}
              onChange={(e) => {
                const picked = assetRows.find((a) => a.key === e.target.value);
                if (picked) onSetRef({ assetKey: picked.key, kind: picked.kind || def.assetKind });
              }}
            >
              {assetRows.map((a) => (
                <MenuItem key={a.id} value={a.key}>
                  {a.title || a.key || '(untitled)'}
                  {a.kind ? ` · ${a.kind}` : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * "Files" authoring panel for a step. Each file the runner uses for this step's kind is a fixed,
 * human-labeled slot (see `FILE_SLOT_CATALOG`) — the author fills each role by uploading a new file
 * or picking an existing campaign asset, and clears it with one button. No slot-string typing and no
 * separate asset-key pointer: the load-bearing slot name is chosen by the role, so a file can never
 * land in a slot the runner does not read.
 *
 * The persisted shape is unchanged (`step.metadata.files` as `StepFileRef[]`, one entry per filled
 * slot): assets remain campaign-level rows, this just references them by their stable key. Any
 * pre-existing reference whose slot is not in this kind's catalog is preserved under "Other file
 * references" so nothing authored earlier (or for another kind) is silently dropped.
 *
 * Purely controlled: binds the `files` array on the parent form's `control`.
 */
export function StepFilesFields(props: {
  control: Control<any>;
  campaignId: number;
  runnerKind?: string;
  disabled?: boolean;
}): JSX.Element {
  const { control, campaignId, runnerKind, disabled } = props;
  const { data: assetRows = [] } = useCampaignAssets(campaignId);
  const assetMut = useCampaignMutations('asset');

  const assetByKey = React.useMemo(() => {
    const m = new Map<string, CampaignAsset>();
    for (const a of assetRows) if (a.key) m.set(a.key, a);
    return m;
  }, [assetRows]);

  const catalog = (runnerKind && FILE_SLOT_CATALOG[runnerKind]) || [];

  // Persist an in-place upload/replace onto the campaign-level asset row (full-record PUT). The step
  // only references the asset by key, so the file bytes belong to the asset, not the step.
  const onAssetUploaded = (asset: CampaignAsset, next: Partial<CampaignAsset>) =>
    assetMut.update.mutate({ ...asset, ...next, id: asset.id });

  // Create a blank campaign asset for a role and return its stable key (or null on failure). The
  // create invalidates the asset list, so the new asset resolves into the slot's `<AssetFileField>`
  // once the list refetches, ready for a drag-drop / file-picker upload.
  const createAsset = async (slot: string, assetKind: string): Promise<string | null> => {
    const key = `${slot}-${Date.now().toString(36)}`;
    try {
      await assetMut.create.mutateAsync({
        campaign: campaignId,
        kind: assetKind,
        key,
        title: '',
        object_key: '',
        content_type: '',
        size_bytes: null,
        order: nextOrderOf(assetRows),
        hidden: false,
        metadata: {},
      });
      return key;
    } catch {
      /* the mutation surfaces its own error state; leave the form unchanged */
      return null;
    }
  };

  return (
    <Stack spacing={1.5} sx={{ pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Files
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Upload the files this step hands to the SE, or pick a file already uploaded elsewhere. Each
        role below maps to a fixed spot in the runner — drag-and-drop or click to upload, preview a
        deck in place, and clear a file with the trash icon.
      </Typography>

      <Controller
        name="files"
        control={control}
        render={({ field }) => {
          const files: StepFileRef[] = Array.isArray(field.value) ? field.value : [];
          const knownSlots = new Set(catalog.map((c) => c.slot));
          // References whose slot this kind's catalog does not cover (legacy data, or a slot added in
          // code but not yet catalogued) — surfaced read-only-ish so they are never silently dropped.
          const extras = files.filter((f) => f.slot && !knownSlots.has(f.slot));

          const refForSlot = (slot: string) => files.find((f) => f.slot === slot);

          // Upsert the single reference for `slot`, then repoint/patch it. One ref per slot.
          const setRef = (slot: string, patch: Partial<StepFileRef> & { assetKey: string }) => {
            const exists = files.some((f) => f.slot === slot);
            const next = exists
              ? files.map((f) => (f.slot === slot ? { ...f, ...patch, slot } : f))
              : [...files, { slot, kind: undefined, ...patch }];
            field.onChange(next);
          };
          const patchRef = (slot: string, patch: Partial<StepFileRef>) =>
            field.onChange(files.map((f) => (f.slot === slot ? { ...f, ...patch } : f)));
          const clearSlot = (slot: string) =>
            field.onChange(files.filter((f) => f.slot !== slot));

          return (
            <Stack spacing={1.5}>
              {catalog.length === 0 && extras.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  This step type doesn&apos;t use file attachments.
                </Typography>
              )}

              {catalog.map((def) => {
                const refRow = refForSlot(def.slot);
                const asset = refRow?.assetKey ? assetByKey.get(refRow.assetKey) : undefined;
                return (
                  <FileSlotRow
                    key={def.slot}
                    def={def}
                    refRow={refRow}
                    asset={asset}
                    assetRows={assetRows}
                    campaignId={campaignId}
                    disabled={disabled}
                    creating={assetMut.isSaving}
                    onCreateAsset={(assetKind) => createAsset(def.slot, assetKind)}
                    onSetRef={(patch) => setRef(def.slot, patch)}
                    onPatchRef={(patch) => patchRef(def.slot, patch)}
                    onClear={() => clearSlot(def.slot)}
                    onAssetUploaded={onAssetUploaded}
                  />
                );
              })}

              {extras.length > 0 && (
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Other file references
                  </Typography>
                  {extras.map((row) => {
                    const asset = row.assetKey ? assetByKey.get(row.assetKey) : undefined;
                    return (
                      <FileSlotRow
                        key={row.slot}
                        def={{ slot: row.slot, label: row.slot, hint: `Slot “${row.slot}” (not used by this step type).`, assetKind: row.kind || 'doc' }}
                        refRow={row}
                        asset={asset}
                        assetRows={assetRows}
                        campaignId={campaignId}
                        disabled={disabled}
                        creating={assetMut.isSaving}
                        onCreateAsset={(assetKind) => createAsset(row.slot, assetKind)}
                        onSetRef={(patch) => setRef(row.slot, patch)}
                        onPatchRef={(patch) => patchRef(row.slot, patch)}
                        onClear={() => clearSlot(row.slot)}
                        onAssetUploaded={onAssetUploaded}
                      />
                    );
                  })}
                </Stack>
              )}
            </Stack>
          );
        }}
      />
    </Stack>
  );
}

export default StepFilesFields;
